import React from "react";
import { motion } from "framer-motion";
import { Zap, IndianRupee, TrendingUp } from "lucide-react";

const highlights = [
  {
    icon: Zap,
    title: "Fast setup — 2 days",
    desc: "Your website, payments and app go live in just 2 days. No long waits, no hidden delays.",
    tone: "from-amber-500/20 to-orange-500/0",
    accent: "text-amber-300",
  },
  {
    icon: IndianRupee,
    title: "Low cost, no surprise fees",
    desc: "Affordable plans designed for Indian shop owners, CSC centers and service providers.",
    tone: "from-emerald-500/20 to-emerald-500/0",
    accent: "text-emerald-300",
  },
  {
    icon: TrendingUp,
    title: "High growth potential",
    desc: "Reach 10× more customers, run offers and watch revenue climb with built-in analytics.",
    tone: "from-violet-500/20 to-pink-500/0",
    accent: "text-violet-300",
  },
];

export default function WhyUs({ onCta, whatsappHref }) {
  return (
    <section
      id="why"
      data-testid="whyus-section"
      className="relative py-24 lg:py-32 overflow-hidden border-y border-white/5"
    >
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90%] h-[80%] radial-glow-cyan blur-3xl opacity-60" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-3xl">
          <p className="uppercase text-xs tracking-[0.24em] text-violet-300 font-semibold">
            Why businesses choose us
          </p>
          <h2
            data-testid="whyus-heading"
            className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight"
          >
            Don't Stay Offline —{" "}
            <span className="text-gradient-neon">Start Earning Online Today.</span>
          </h2>
          <p className="mt-4 text-slate-400 max-w-xl leading-relaxed">
            Join thousands of Indian businesses already growing online with
            Digital Onboarding.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
          {highlights.map((h, i) => (
            <motion.div
              key={h.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.55, delay: i * 0.1 }}
              data-testid={`why-card-${i}`}
              className="relative overflow-hidden rounded-2xl glass border border-white/5 p-7 hover:-translate-y-1 hover:border-violet-500/40 transition-all"
            >
              <div className={`absolute -top-20 -right-20 w-52 h-52 bg-gradient-to-br ${h.tone} rounded-full blur-3xl opacity-70`} />
              <div className="relative">
                <div className="w-12 h-12 rounded-xl glass flex items-center justify-center mb-5">
                  <h.icon className={`w-5 h-5 ${h.accent}`} />
                </div>
                <h3 className="font-display text-xl font-bold">{h.title}</h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{h.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mt-14 flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <button
            onClick={onCta}
            data-testid="whyus-cta-button"
            className="group px-7 py-3.5 rounded-full btn-primary-grad text-white font-semibold text-base transition-all hover:-translate-y-0.5 shadow-2xl shadow-violet-500/40"
          >
            Start Now — Get Online in 24 Hours
          </button>
          <a
            href={whatsappHref}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="whyus-whatsapp-cta"
            className="px-7 py-3.5 rounded-full glass hover:bg-white/10 text-white font-semibold text-base transition-all flex items-center justify-center gap-2 border border-white/10"
          >
            WhatsApp: 9141302057
          </a>
        </motion.div>
      </div>
    </section>
  );
}
